import { Link } from "react-router-dom";
import { Shield, Globe } from "lucide-react";
import { useTranslation } from "@/hooks/useTranslation";
import { isLyrraOne } from "@/lib/platform";
import { useLyrraOneRegional } from "@/hooks/useLyrraOneRegional";
import { LyrraOneLogo } from "@/components/brand/LyrraOneLogo";
import { LyrraOneRegionSelector } from "@/components/public/LyrraOneRegionSelector";

export function ModernFooter() {
  const { t } = useTranslation();
  const { tx: ftr } = useLyrraOneRegional('lyrraoneFooter');
  const year = new Date().getFullYear().toString();

  if (isLyrraOne()) {
    return (
      <footer className="bg-white border-t border-[#E2E8F0]" aria-label="Pied de page" data-marketing-footer>
        <div className="max-w-7xl mx-auto px-4 py-14">
          <div className="grid lg:grid-cols-4 gap-8">
            <div className="lg:col-span-1">
              <LyrraOneLogo size="lg" linkTo="/" />
              <p className="text-[#475569] text-sm leading-relaxed mt-5">{ftr('description')}</p>
              <p className="text-[#2563EB] text-sm font-medium mt-3">{ftr('tagline')}</p>
            </div>
            <div>
              <h3 className="font-semibold mb-5 text-[#0F172A]" style={{ fontFamily: 'Outfit, sans-serif' }}>{ftr('product.title')}</h3>
              <ul className="space-y-2.5 text-sm">
                <li><Link to="/features" className="text-[#475569] hover:text-[#2563EB] transition-colors">{ftr('product.features')}</Link></li>
                <li><Link to="/store" className="text-[#475569] hover:text-[#2563EB] transition-colors">{ftr('product.marketplace')}</Link></li>
                <li><Link to="/demo" className="text-[#475569] hover:text-[#2563EB] transition-colors">{ftr('product.demo')}</Link></li>
                <li><Link to="/pricing" className="text-[#475569] hover:text-[#2563EB] transition-colors">{ftr('product.pricing')}</Link></li>
                <li><Link to="/accessibility" className="text-[#475569] hover:text-[#2563EB] transition-colors">{ftr('product.accessibility')}</Link></li>
                <li><Link to="/aide" className="text-[#475569] hover:text-[#2563EB] transition-colors">{ftr('product.help')}</Link></li>
              </ul>
            </div>
            <div>
              <h3 className="font-semibold mb-5 text-[#0F172A]" style={{ fontFamily: 'Outfit, sans-serif' }}>{ftr('company.title')}</h3>
              <ul className="space-y-2.5 text-sm">
                <li><Link to="/about" className="text-[#475569] hover:text-[#2563EB] transition-colors">{ftr('company.about')}</Link></li>
                <li><Link to="/contact" className="text-[#475569] hover:text-[#2563EB] transition-colors">{ftr('company.contact')}</Link></li>
              </ul>
            </div>
            <div>
              <h3 className="font-semibold mb-5 text-[#0F172A]" style={{ fontFamily: 'Outfit, sans-serif' }}>{ftr('legal.title')}</h3>
              <ul className="space-y-2.5 text-sm">
                <li><Link to="/privacy" className="text-[#475569] hover:text-[#2563EB] transition-colors">{ftr('legal.privacy')}</Link></li>
                <li><Link to="/terms" className="text-[#475569] hover:text-[#2563EB] transition-colors">{ftr('legal.terms')}</Link></li>
                <li><Link to="/cookies" className="text-[#475569] hover:text-[#2563EB] transition-colors">{ftr('legal.cookies')}</Link></li>
              </ul>
            </div>
          </div>
          <div className="mt-10 hidden border-t border-[#E2E8F0] pt-8 sm:block">
            <LyrraOneRegionSelector />
          </div>
          <div className="border-t border-[#E2E8F0] pt-8 mt-8 sm:mt-8 flex flex-col sm:flex-row justify-between items-center gap-4 text-sm text-[#64748B]">
            <p>{ftr('copyright').replace('{{year}}', year)}</p>
            <div className="flex items-center gap-4">
              <span className="flex items-center gap-1.5"><Shield className="w-4 h-4 text-[#10B981]" aria-hidden />{ftr('security.ssl')}</span>
              <span className="flex items-center gap-1.5"><Globe className="w-4 h-4 text-[#2563EB]" aria-hidden />{ftr('security.gdpr')}</span>
            </div>
          </div>
        </div>
      </footer>
    );
  }

  return (
    <footer className="bg-[#F8FAFC] border-t border-[#E2E8F0]" aria-label="Pied de page" data-marketing-footer>
      <div className="max-w-7xl mx-auto px-4 py-16">
        <div className="grid lg:grid-cols-4 gap-8">
          <div className="lg:col-span-1">
            <div className="flex items-center gap-2 mb-6">
              <div className="flex flex-col">
                <img src="/LogoSmall.png" alt="LyrraOne" className="h-7" />
              </div>
            </div>
            <p className="text-[#475569] text-sm leading-relaxed">
              {t('publicFooter.description')}
            </p>
          </div>
          <div>
            <h3 className="text-[#0F172A] font-semibold mb-6">{t('publicFooter.product.title')}</h3>
            <ul className="space-y-3">
              <li><Link to="/pricing" className="text-[#475569] hover:text-[#1D4ED8] transition-colors">{t('publicFooter.product.pricing')}</Link></li>
              <li><Link to="/dashboard" className="text-[#475569] hover:text-[#1D4ED8] transition-colors">{t('publicFooter.product.dashboard')}</Link></li>
              <li><Link to="/aide" className="text-[#475569] hover:text-[#1D4ED8] transition-colors">{t('publicFooter.product.help')}</Link></li>
            </ul>
          </div>
          <div>
            <h3 className="text-[#0F172A] font-semibold mb-6">{t('publicFooter.company.title')}</h3>
            <ul className="space-y-3">
              <li><Link to="/about" className="text-[#475569] hover:text-[#1D4ED8] transition-colors">{t('publicFooter.company.about')}</Link></li>
              <li><Link to="/contact" className="text-[#475569] hover:text-[#1D4ED8] transition-colors">{t('publicFooter.company.contact')}</Link></li>
              <li><Link to="/partners" className="text-[#475569] hover:text-[#1D4ED8] transition-colors">{t('publicFooter.company.partners')}</Link></li>
            </ul>
          </div>
          <div>
            <h3 className="text-[#0F172A] font-semibold mb-6">{t('publicFooter.legal.title')}</h3>
            <ul className="space-y-3">
              <li><Link to="/privacy" className="text-[#475569] hover:text-[#1D4ED8] transition-colors">{t('publicFooter.legal.privacy')}</Link></li>
              <li><Link to="/terms" className="text-[#475569] hover:text-[#1D4ED8] transition-colors">{t('publicFooter.legal.terms')}</Link></li>
              <li><Link to="/cookies" className="text-[#475569] hover:text-[#1D4ED8] transition-colors">{t('publicFooter.legal.cookies')}</Link></li>
            </ul>
          </div>
        </div>
        <div className="hidden lg:flex justify-end mt-8">
          <img src="/FT_logo240.png" alt="La French Tech" className="w-28 h-auto object-contain opacity-70" />
        </div>
        <div className="border-t border-[#E2E8F0] pt-8 mt-6">
          <div className="flex flex-col lg:flex-row justify-between items-center gap-4">
            <div className="mb-2 flex flex-col gap-3 text-center sm:flex-row sm:items-center sm:gap-6 lg:mb-0 sm:text-start">
              <p className="text-[#475569] text-sm">{t('publicFooter.copyright')}</p>
              <div className="flex flex-wrap items-center justify-center sm:justify-start gap-x-4 gap-y-2">
                <Link to="/privacy" className="text-[#475569] hover:text-[#1D4ED8] text-sm transition-colors">{t('publicFooter.legal.privacy')}</Link>
                <Link to="/terms" className="text-[#475569] hover:text-[#1D4ED8] text-sm transition-colors">{t('publicFooter.legal.terms')}</Link>
                <Link to="/cookies" className="text-[#475569] hover:text-[#1D4ED8] text-sm transition-colors">{t('publicFooter.legal.cookies')}</Link>
              </div>
            </div>
            <div className="flex flex-wrap items-center justify-center gap-3">
              <div className="flex items-center gap-2">
                <Shield className="w-4 h-4 text-[#10B981]" aria-hidden="true" />
                <span className="text-sm text-[#475569]">{t('publicFooter.security.ssl')}</span>
              </div>
              <div className="flex items-center gap-2">
                <Globe className="w-4 h-4 text-[#1D4ED8]" aria-hidden="true" />
                <span className="text-sm text-[#475569]">{t('publicFooter.security.gdpr')}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}
